import { Component } from '@theme/component';

/**
 * Cached Swiper constructor — first instance imports, subsequent instances reuse.
 * @type {typeof import('swiper').default | null}
 */
let cachedSwiper = null;

const loadSwiper = async () => {
  if (!cachedSwiper) {
    const { default: Swiper } = await import('swiper');
    cachedSwiper = Swiper;
  }
  return cachedSwiper;
};

/**
 * `<card-carousel>` is the section's full slideshow + video orchestrator.
 *
 * It owns Swiper init, click-to-center, ARIA, loop-clone management,
 * video playback orchestration, and ±1 adjacent-slide prewarm so the
 * next/prev slide's video is already mid-fetch by the time the user
 * navigates. This does NOT use
 * slideshow-component / slideshow.js — they handle every other slideshow
 * on the site, but the card-carousel needs special behavior (per-slide video
 * coordination, transform-based loop with centeredSlides, scale effects)
 * and lives entirely in this file.
 *
 * Interaction with media.js is one-way: card-carousel calls the public
 * `<deferred-media>` API (`playMedia`, `pauseMedia`, `applyCarouselMuteState`)
 * to drive each slide's video. The deferred-media instance opts out of
 * media.js's autoplay self-management when it sees a `card-carousel`
 * ancestor (see media.js connectedCallback).
 *
 * @typedef {Object} CardCarouselRefs
 * @property {HTMLElement} scroller         - The Swiper root (`.swiper`)
 * @property {HTMLElement[]} [slides]       - Each `.swiper-slide` (cc-item)
 * @property {HTMLButtonElement} [previous] - Previous arrow
 * @property {HTMLButtonElement} [next]     - Next arrow
 *
 * @extends {Component<CardCarouselRefs>}
 */
export class CardCarousel extends Component {
  requiredRefs = ['scroller'];

  /** @type {InstanceType<typeof import('swiper').default> | null} */
  #swiper = null;

  /** @type {AbortController} Recreated on every connect — Swiper's loop mode
   * moves slide DOM nodes across the wrapper, which fires
   * disconnect → reconnect on this custom element. Reusing an aborted
   * controller would silently drop every listener attached in the second
   * connect. */
  #abortController = new AbortController();


  /** @type {boolean} Whether we should apply center-scale transforms */
  #centerScale = false;

  /** @type {boolean} */
  #isLoop = false;

  /** Cache of per-slide element refs. Without this, #applyVisualEffects
   * runs `slide.querySelector('.cc-item__card')` and `.cc-item__above-text`
   * for every slide every frame during a drag — Safari's slower DOM
   * lookups + style invalidation amplify this into visible jank.
   * `media` is the per-slide <deferred-media> (or null for non-video
   * slides) — cached so the per-frame data-show-play-icon write doesn't
   * re-query each slide's subtree every Swiper setTranslate frame.
   * @type {WeakMap<HTMLElement, { card: HTMLElement | null, aboveText: HTMLElement | null, media: HTMLElement | null }>} */
  #slideRefs = new WeakMap();

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    // Defer Swiper bundle download (151KB) AND the active slide's
    // playMedia call (which fetches the active mini's source) until
    // after window.load. This guarantees DCL + load fire before
    // below-the-fold carousel media even starts downloading, freeing
    // bandwidth for the hero. If the page is already loaded (e.g.
    // a SPA-style section reload), init synchronously.
    if (document.readyState === 'complete') {
      this.#init();
    } else {
      window.addEventListener('load', () => {
        if (this.isConnected) this.#init();
      }, { once: true, signal: this.#abortController.signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#swiper) {
      // cleanStyles=true so inline styles set by Swiper (margin-right for
      // spaceBetween, wrapper transform) are removed — otherwise reinit
      // can stack styles and produce double-gaps or stale layout.
      this.#swiper.destroy(true, true);
      this.#swiper = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Public API — invoked declaratively by the arrow snippet's on:click
  // ---------------------------------------------------------------------------

  /**
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    this.#swiper?.slidePrev(500);
  }

  /**
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    this.#swiper?.slideNext(500);
  }

  /**
   * Triggers metadata prewarm of the current active slide's ±1 neighbors.
   * Called by media.js after the user unmutes (the mute-state change sets
   * `data-unmuted` on the carousel container, which the prewarm logic
   * gates on). Without this hook the first post-unmute slide-navigation
   * would still hit a cold cache; subsequent slide changes are covered
   * by the existing #syncActiveCarouselVideo flow.
   */
  prewarmCurrentAdjacent() {
    if (!this.#swiper) return;
    this.#prewarmAdjacentSlides(this.#swiper.activeIndex);
  }

  // ---------------------------------------------------------------------------
  // Swiper init
  // ---------------------------------------------------------------------------

  async #init() {
    const scroller = this.refs.scroller;
    if (!scroller) return;

    this.#centerScale = this.hasAttribute('data-center-scale');
    this.#isLoop = this.hasAttribute('loop');

    const initialSlide = parseInt(this.getAttribute('initial-slide') || '0', 10) || 0;
    const minimumForLoop = parseInt(this.getAttribute('minimum-for-loop') || '0', 10);

    if (this.#isLoop && minimumForLoop > 0) {
      this.#ensureLoopSlides(scroller, minimumForLoop);
    }

    const Swiper = await loadSwiper();
    if (!this.isConnected) return;

    const gap = parseInt(getComputedStyle(this).getPropertyValue('--cc-gap'), 10) || 16;

    const config = {
      wrapperClass: 'swiper-wrapper',
      slideClass: 'swiper-slide',
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: gap,
      loop: this.#isLoop,
      loopAdditionalSlides: 3,
      initialSlide,
      speed: 500,
      grabCursor: true,
      simulateTouch: true,
      watchSlidesProgress: true,
      threshold: 15,
      touchAngle: 35,
      touchStartPreventDefault: false,
      touchReleaseOnEdges: true,
      mousewheel: { forceToAxis: true },
      on: {
        // setTranslate runs on every transform update (drag every frame +
        // once per snap target). Cheap visual effects only — opacity, scale,
        // [data-active]. Video play/pause is decoupled from this handler
        // because intermediate slides briefly cross the active threshold
        // during fast snap-throughs and would start playing.
        setTranslate: () => this.#applyVisualEffects(),
        setTransition: (_, dur) => this.#setEffectTransitions(dur),

        // slideChange (immediate) AND slideChangeTransitionEnd (settled)
        // both fire #syncActiveCarouselVideo. It's idempotent and covers:
        //   - slideChange catches loopFix shifts that suppress
        //     slideChangeTransitionEnd
        //   - slideChangeTransitionEnd catches the settled active when an
        //     interrupted transition resolves
        slideChange: () => {
          // Update data-active FIRST (before pauseMedia in #syncActiveCarouselVideo).
          // pauseMedia fires the video's 'pause' event, which sets data-paused on
          // the deferred-media. If data-active is still on the old active at that
          // moment, the global [data-paused] rule briefly shows the state-indicator
          // play icon on the still-data-active card — a flicker on previously-active
          // cards during slide changes.
          this.#applyVisualEffects();
          this.#syncActiveCarouselVideo();
        },
        slideChangeTransitionEnd: () => {
          this.#applyVisualEffects();
          this.#syncActiveCarouselVideo();
        },
        transitionStart: () => this.setAttribute('transitioning', ''),
        transitionEnd: () => this.removeAttribute('transitioning'),
        // is-dragging toggle: lets CSS suppress expensive paint costs
        // (backdrop-filter, drop-shadow on the active card's mute toggle)
        // while the user is actively dragging. Safari does not GPU-
        // accelerate filter/backdrop-filter — animating ancestors of a
        // blurred element forces CPU repaint of the blur every frame.
        // See https://bugs.webkit.org/show_bug.cgi?id=89475.
        touchStart: () => this.setAttribute('data-dragging', ''),
        touchEnd: () => this.removeAttribute('data-dragging'),
      },
    };

    this.#swiper = new Swiper(scroller, config);

    // Click-to-center for inactive slides. Triggers play synchronously
    // inside the click's user-gesture context (see comment below).
    scroller.addEventListener('click', this.#handleScrollerClick, {
      signal: this.#abortController.signal,
    });

    // Single tab stop with arrow-key navigation
    scroller.setAttribute('tabindex', '0');
    scroller.setAttribute('role', 'region');
    scroller.setAttribute('aria-roledescription', 'carousel');
    scroller.setAttribute('aria-label', 'Card carousel');
    scroller.addEventListener('keydown', this.#handleScrollerKeydown, {
      signal: this.#abortController.signal,
    });

    if (this.#isLoop) this.#inertLoopClones();

    // Initial state — apply visuals + start the centered video.
    this.#applyVisualEffects();
    this.#syncActiveCarouselVideo();

    // Post-layout settle: Swiper's `update()` recomputes spacing/progress
    // against the post-layout DOM. Without this, the first paint can land
    // before fonts/images settle and slide widths look stale.
    requestAnimationFrame(() => {
      if (!this.#swiper || !this.isConnected) return;
      this.#swiper.update();
      this.#applyVisualEffects();
    });
  }

  /**
   * Ensures enough slide DOM nodes exist for smooth loop mode.
   * Always clones originals in WHOLE cycles — partial cycles would
   * leave the last batch truncated (e.g. 6 originals padded to 20 ends
   * mid-cycle at "A B"), which breaks visual continuity at the loop
   * boundary: scrolling past the final original lands on Swiper's
   * end-clone of position 0, so the user sees the same video repeat
   * adjacent to itself ("...A B A B...").
   * @param {HTMLElement} scroller
   * @param {number} minimum
   */
  #ensureLoopSlides(scroller, minimum) {
    const wrapper = scroller.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const originals = /** @type {HTMLElement[]} */ ([
      ...wrapper.querySelectorAll(':scope > .swiper-slide'),
    ]);
    if (!originals.length) return;

    let total = originals.length;
    if (total >= minimum) return;

    while (total < minimum) {
      for (const slide of originals) {
        const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
        clone.removeAttribute('id');
        clone.removeAttribute('slide-id');
        clone.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(clone);
        total++;
      }
    }
  }

  /**
   * Marks Swiper's loop duplicate slides as `inert` so keyboard users can't
   * land in infinite clones during tab navigation.
   */
  #inertLoopClones() {
    if (!this.#swiper) return;
    for (const slide of this.#swiper.slides) {
      if (slide.classList.contains('swiper-slide-duplicate')) {
        /** @type {any} */ (slide).inert = true;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Visual effects (active state, opacity, scale)
  // ---------------------------------------------------------------------------

  #applyVisualEffects() {
    if (!this.#swiper) return;

    const withScale = this.#centerScale;
    const activeIdx = this.#swiper.activeIndex;
    const slides = this.#swiper.slides;

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      const progress = /** @type {any} */ (slide).progress;
      if (progress === undefined) continue;

      const distance = Math.abs(progress);
      // data-active follows Swiper's activeIndex (the snap target), not
      // a progress threshold. setTranslate fires per-frame during snap
      // and Swiper's loopFix can briefly leave the new active's
      // progress > 0.5, which under a threshold check left zero slides
      // with data-active mid-transition — making every card render its
      // inactive-state play icon for the rest of the animation.
      const isActive = i === activeIdx;

      if (isActive) {
        slide.setAttribute('data-active', '');
      } else {
        slide.removeAttribute('data-active');
      }

      // Opacity: 1.0 when centered, 0.75 when 1+ slides away
      /** @type {HTMLElement} */ (slide).style.opacity = `${Math.max(0.75, 1 - distance * 0.25)}`;

      let refs = this.#slideRefs.get(slide);
      if (!refs) {
        refs = {
          card: slide.querySelector('.cc-item__card'),
          aboveText: slide.querySelector('.cc-item__above-text'),
          media: slide.querySelector('deferred-media[autoplay]'),
        };
        this.#slideRefs.set(slide, refs);
      }

      // Above-text fades through the transition
      if (refs.aboveText) {
        const textOpacity = Math.max(0, 1 - distance * 3);
        refs.aboveText.style.opacity = `${textOpacity}`;
        refs.aboveText.style.pointerEvents = textOpacity > 0.5 ? '' : 'none';
      }

      if (withScale && refs.card) {
        const scaleEnd = 0.2;
        const scaleRange = 1.0 - scaleEnd;
        const centerProgress = 1 - Math.min(1, Math.max(0, (distance - scaleEnd) / scaleRange));
        const scale = 1 + 0.12 * centerProgress;
        const direction = progress > 0 ? -1 : 1;
        const translateX = isActive ? 0 : direction * Math.min(1, distance) * 6;
        refs.card.style.transform = `scale(${scale}) translateX(${translateX}%)`;
        // Publish the parent scale as a CSS variable so the centered
        // .video-state-indicator (which lives inside the scaled subtree)
        // can counter-scale to a constant 56px visual size — see
        // snippets/video.liquid. Without this the icon visibly grows
        // 1.0→1.12 along with the card during the snap.
        refs.card.style.setProperty('--cc-card-scale', String(scale));
      } else if (refs.card) {
        refs.card.style.transform = '';
        refs.card.style.removeProperty('--cc-card-scale');
      }

      // Persistent play-icon visibility on inactive slides.
      // The play icon is the "click to focus this card" affordance for
      // every non-active slide. Authority lives here (carousel) and in
      // media.js's play/pause/blocked listeners — this attribute is the
      // SINGLE driver for .video-state-indicator visibility (see CSS in
      // snippets/video.liquid). For active slides, we deliberately do
      // NOT clear the attribute: the state machine wants the icon to
      // stay visible from the moment a card is clicked (when it was
      // inactive and had the attribute) all the way through the 500ms
      // snap until the video's first frame paints. media.js's 'playing'
      // listener clears it then, in lockstep with the unmute toggle's
      // fade-in — a clean visual handoff with no other UI between them.
      if (refs.media) {
        if (isActive) {
          // Don't touch — let media.js manage via play/pause events.
        } else {
          refs.media.setAttribute('data-show-play-icon', '');
        }
      }
    }
  }

  /**
   * Sync CSS transition durations on visual-effect elements with Swiper's
   * own transition timing. Drag = 0ms (instant), snap = e.g. 500ms.
   * @param {number} duration
   */
  #setEffectTransitions(duration) {
    if (!this.#swiper) return;
    const timing = `${duration}ms ease-out`;
    for (const slide of this.#swiper.slides) {
      /** @type {HTMLElement} */ (slide).style.transition = `opacity ${timing}`;

      let refs = this.#slideRefs.get(slide);
      if (!refs) {
        refs = {
          card: slide.querySelector('.cc-item__card'),
          aboveText: slide.querySelector('.cc-item__above-text'),
          media: slide.querySelector('deferred-media[autoplay]'),
        };
        this.#slideRefs.set(slide, refs);
      }
      if (refs.aboveText) refs.aboveText.style.transition = `opacity ${timing}`;
      if (refs.card) refs.card.style.transition = `transform ${timing}`;
    }
  }

  // ---------------------------------------------------------------------------
  // Video orchestration — single source of truth for which slide plays
  // ---------------------------------------------------------------------------

  /**
   * Pauses every slide except the centered one, then plays the centered slide.
   * Idempotent: safe to call from both slideChange and slideChangeTransitionEnd.
   *
   * Pre-warms ±3 adjacent slides on a 400ms delay so:
   *   - the active video gets first claim on bandwidth (key on slow networks)
   *   - rapid swipes don't pile up parallel fetches that starve playback
   */
  #syncActiveCarouselVideo() {
    if (!this.#swiper) return;
    const activeIdx = this.#swiper.activeIndex;
    const activeSlide = this.#swiper.slides[activeIdx];

    for (const slide of this.#swiper.slides) {
      if (slide === activeSlide) continue;
      const media = /** @type {any} */ (slide.querySelector('deferred-media[autoplay]'));
      if (media?.pauseMedia) media.pauseMedia();
      // Proactively strip the inner <video>'s autoplay attribute so
      // Safari can't spuriously re-attempt playback on this paused
      // inactive slide when its compositor layer is invalidated by
      // the next Swiper snap. playMedia restores it when the slide
      // becomes active again. Belt-and-suspenders alongside media.js's
      // 'playing' guard — this prevents the play attempt entirely
      // rather than catching it after the fact.
      const video = /** @type {HTMLVideoElement | null} */ (
        media?.querySelector?.('video:not([data-prewarm])')
      );
      if (video) video.removeAttribute('autoplay');
    }

    if (!activeSlide) return;
    const activeMedia = /** @type {any} */ (activeSlide.querySelector('deferred-media[autoplay]'));
    if (activeMedia?.playMedia) activeMedia.playMedia();

    // Prewarm ±1 neighbors so the next/prev slide's video is already
    // mid-fetch by the time the user navigates. We hydrate the cloned
    // <video> element (with `autoplay` attribute stripped so playback
    // doesn't start) and let the browser's normal preload="auto" trigger
    // the Range request. No load() calls, no preload mutation — those
    // were the things that aborted in-flight fetches and caused the
    // "10 second wait" we shipped earlier fixes for.
    //
    // Range is intentionally ±1 (not ±2+). HTTP/1.1 caps connections
    // per domain at 6; ±1 = 3 concurrent in-flight (active + prev + next)
    // and leaves plenty of headroom for assets/CSS.
    this.#prewarmAdjacentSlides(activeIdx);
  }

  /**
   * @param {number} activeIdx
   */
  #prewarmAdjacentSlides(activeIdx) {
    if (!this.#swiper) return;
    const slides = this.#swiper.slides;
    const containerUnmuted = this.hasAttribute('data-unmuted');

    for (const off of /** @type {const} */ ([1, -1])) {
      const slide = slides[activeIdx + off];
      if (!slide) continue;

      const media = /** @type {HTMLElement | null} */ (slide.querySelector('deferred-media[autoplay]'));
      if (!media) continue;

      // Mini-mode slides: prewarm the FULL video's metadata only when the
      // carousel is in unmuted state — that's the signal that future slide
      // navigations will hydrate the full unmuted, so getting metadata +
      // first segment cached now makes the next swap noticeably faster.
      // We mark the prewarm element with `data-prewarm` so #hydrateFullVideo
      // (in media.js) can promote it instead of cloning the template again.
      const fullTemplate = /** @type {HTMLTemplateElement | null} */ (
        media.querySelector('template[data-full-video]')
      );
      if (fullTemplate) {
        if (!containerUnmuted) continue;
        if (media.querySelector('video[data-prewarm]')) continue;

        const fullContent = fullTemplate.content.firstElementChild?.cloneNode(true);
        if (!(fullContent instanceof HTMLVideoElement)) continue;

        fullContent.removeAttribute('autoplay');
        fullContent.setAttribute('preload', 'metadata');
        fullContent.setAttribute('data-prewarm', '');
        // Hidden so the prewarm element doesn't visually compete with the
        // mini playing on top — it's purely a network-cache primer.
        fullContent.style.display = 'none';

        // Insert before the mini so DOM order matches what hydrate would
        // produce (full before mini → mini wins via DOM order at same z-index).
        const miniEl = media.querySelector('.deferred-media__mini-video');
        if (miniEl && miniEl.parentElement === media) {
          media.insertBefore(fullContent, miniEl);
        } else {
          media.appendChild(fullContent);
        }
        continue;
      }

      // Non-mini path (legacy defer_video pattern): hydrate the template
      // into DOM so preload="auto" triggers a Range request.
      if (media.getAttribute('data-media-loaded')) continue;
      if (media.querySelector('video')) continue;

      const tpl = /** @type {HTMLTemplateElement | null} */ (media.querySelector('template'));
      const content = tpl?.content?.firstElementChild?.cloneNode(true);
      if (!content) continue;

      if (content instanceof HTMLVideoElement) {
        content.removeAttribute('autoplay');
      }

      media.setAttribute('data-media-loaded', 'true');
      media.appendChild(content);
    }
  }

  // ---------------------------------------------------------------------------
  // Scroller interaction — click-to-center + keyboard nav
  // ---------------------------------------------------------------------------

  /**
   * Click-to-center on inactive slides. We trigger playMedia synchronously
   * inside the user-gesture context for two reasons:
   *   1. After slideToLoop in loop mode, Swiper's loopFix sometimes
   *      suppresses slideChangeTransitionEnd — relying on it would leave
   *      the new active video paused with the poster overlay still up.
   *   2. The 500ms slide-to animation can outlive the gesture window, so
   *      any unmuted playMedia at transitionEnd would be blocked by the
   *      browser's autoplay policy.
   * @param {Event} e
   */
  #handleScrollerClick = (e) => {
    const slide = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (e.target)?.closest('.swiper-slide')
    );
    if (!slide || !this.#swiper) return;

    const clickedIndex = this.#swiper.slides.indexOf(slide);
    if (clickedIndex === -1 || clickedIndex === this.#swiper.activeIndex) return;

    const targetMedia = /** @type {any} */ (slide.querySelector('deferred-media[autoplay]'));
    if (targetMedia) {
      // No data-paused hack here anymore. The clicked slide already has
      // [data-show-play-icon] set on it (every inactive slide carries it,
      // courtesy of #applyVisualEffects). When this slide becomes active
      // we deliberately leave the attribute alone — the icon stays
      // visible across the 500ms snap. media.js's 'playing' listener
      // removes the attribute the moment the first frame paints, in
      // lockstep with the unmute toggle's fade-in.
      if (targetMedia.playMedia) targetMedia.playMedia(true);
    }

    if (this.#swiper.params.loop) {
      const realIndex = parseInt(slide.getAttribute('data-swiper-slide-index') || '', 10);
      if (!isNaN(realIndex)) {
        this.#swiper.slideToLoop(realIndex, 500);
        return;
      }
    }

    this.#swiper.slideTo(clickedIndex, 500);
  };

  /**
   * @param {KeyboardEvent} e
   */
  #handleScrollerKeydown = (e) => {
    if (!this.#swiper) return;
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    // Same rationale as #handleScrollerClick — no data-paused hack.
    // The about-to-be-active slide already carries [data-show-play-icon]
    // from #applyVisualEffects, so the icon stays visible across the
    // snap until media.js clears it on first frame.
    if (e.key === 'ArrowRight') this.#swiper.slideNext(500);
    else this.#swiper.slidePrev(500);
  };
}

if (!customElements.get('card-carousel')) {
  customElements.define('card-carousel', CardCarousel);
}
